// packages/ui/src/tools/terminal/sessionRegistry.ts
import type { PtySession } from '@slate/core';

/**
 * Live `PtySession` handles keyed by tool-instance id. `PtySession` is not
 * serializable, so it can't live in the Zustand tools store alongside the
 * instance metadata; this module-level registry is the side channel that lets
 * the window-close flow reach every running shell and kill it before the app
 * exits. Without it, closing the window would leave orphaned PTY child
 * processes behind.
 *
 * `TerminalView` registers its session once spawned and removes it on unmount;
 * `App.tsx`'s close handler calls {@link killAll} so neither busy nor idle
 * terminals leak a shell.
 */
const map = new Map<string, PtySession>();

export const terminalSessionRegistry = {
  set(instanceId: string, session: PtySession): void {
    map.set(instanceId, session);
  },
  delete(instanceId: string): void {
    map.delete(instanceId);
  },
  get(instanceId: string): PtySession | undefined {
    return map.get(instanceId);
  },
  /**
   * Kill every registered session and clear the registry. Each kill is
   * fault-isolated so one already-dead session can't prevent the rest from
   * being reaped (and can't trap the user in the app during close).
   */
  killAll: async (): Promise<void> => {
    const sessions = Array.from(map.values());
    map.clear();
    await Promise.all(sessions.map((s) => s.kill().catch(() => undefined)));
  },
};
